import type { CurrencyCode } from '../money/currency';
import { Money, type Money as MoneyValue } from '../money/money';
import { DEFAULT_ROUNDING, type RoundingMode } from '../money/rounding';
import type {
  LineItemInput,
  LineItemResult,
  PerLineTax,
  TaxBreakdown,
  TaxConfig,
} from './types';

interface TaxContext {
  readonly currency: CurrencyCode;
  readonly mode: RoundingMode;
  readonly lines: readonly LineItemResult[];
  readonly inputs: readonly LineItemInput[];
  /**
   * Ratio applied to each line's taxable amount to reflect the
   * invoice-level discount (so tax stays proportional). `1` when there
   * is no invoice-level discount.
   */
  readonly taxableRatioNum: bigint;
  readonly taxableRatioDen: bigint;
  /**
   * Sum of per-line taxable amounts after applying the invoice-discount
   * ratio. This is the actual base the tax rate is applied to; it
   * excludes non-taxable line totals.
   */
  readonly taxableBase: MoneyValue;
}

function validateRate(rate: number | string, label: string): number {
  const num = typeof rate === 'number' ? rate : Number(rate);
  if (!Number.isFinite(num) || num < 0) {
    throw new Error(`${label}: invalid rate ${String(rate)}`);
  }
  return num;
}

type RatioCtx = Pick<
  TaxContext,
  'currency' | 'mode' | 'taxableRatioNum' | 'taxableRatioDen'
>;

function perLineTaxable(line: LineItemResult, ctx: RatioCtx): MoneyValue {
  if (Money.isZero(line.taxable)) return Money.zero(ctx.currency);
  if (ctx.taxableRatioDen === 1n && ctx.taxableRatioNum === 1n) return line.taxable;
  // taxable × num / den, rounded.
  const scaled = Money.mul(line.taxable, `${ctx.taxableRatioNum}`, ctx.mode);
  return Money.div(scaled, `${ctx.taxableRatioDen}`, ctx.mode);
}

export function applyTax(
  config: TaxConfig,
  lines: readonly LineItemResult[],
  inputs: readonly LineItemInput[],
  subtotal: MoneyValue,
  discountedSubtotal: MoneyValue,
  currency: CurrencyCode,
  mode: RoundingMode = DEFAULT_ROUNDING,
): TaxBreakdown {
  // Ratio of (post-invoice-discount subtotal) to (pre-invoice-discount
  // subtotal). Expressed as bigints so we never lose precision when
  // distributing the discount's effect across line-level taxable
  // amounts.
  const ratioDen = subtotal.amount === 0n ? 1n : subtotal.amount;
  const ratioNum = subtotal.amount === 0n ? 1n : discountedSubtotal.amount;

  const preCtx = {
    currency,
    mode,
    lines,
    inputs,
    taxableRatioNum: ratioNum,
    taxableRatioDen: ratioDen,
  } as const;

  const perLineTaxables = lines.map((l) => perLineTaxable(l, preCtx));
  const taxableBase = Money.sum(perLineTaxables.length ? perLineTaxables : [Money.zero(currency)]);

  const ctx: TaxContext = { ...preCtx, taxableBase };

  switch (config.method) {
    case 'none':
      return noneTax(ctx);
    case 'on_total':
      return onTotalTax(ctx, config);
    case 'per_item':
      return perItemTax(ctx, config);
    case 'as_deduction':
      return asDeductionTax(ctx, config);
  }
}

function noneTax(ctx: TaxContext): TaxBreakdown {
  return {
    method: 'none',
    inclusive: false,
    total: Money.zero(ctx.currency),
    perLine: ctx.lines.map((l) => ({
      id: l.id,
      tax: Money.zero(ctx.currency),
      netAfterTax: l.net,
    })),
  };
}

function onTotalTax(
  ctx: TaxContext,
  config: Extract<TaxConfig, { method: 'on_total' }>,
): TaxBreakdown {
  validateRate(config.rate, 'on_total');
  const rateStr = `${config.rate}`;

  if (config.inclusive) {
    // taxableBase already includes tax. Back it out:
    //   taxExcl = base × 100 / (100 + rate)
    //   tax     = base − taxExcl
    const divisorPercent = `${100 + Number(config.rate)}`;
    const taxExcl = Money.div(
      Money.mul(ctx.taxableBase, '100', ctx.mode),
      divisorPercent,
      ctx.mode,
    );
    const taxTotal = Money.sub(ctx.taxableBase, taxExcl);
    const perLine = allocateAcrossLines(ctx, taxTotal, /* netIsTaxInclusive */ true);
    return {
      method: 'on_total',
      label: config.label,
      rate: config.rate,
      inclusive: true,
      total: taxTotal,
      perLine,
    };
  }

  // Exclusive: tax = taxableBase × rate / 100.
  const taxTotal = Money.div(
    Money.mul(ctx.taxableBase, rateStr, ctx.mode),
    '100',
    ctx.mode,
  );
  const perLine = allocateAcrossLines(ctx, taxTotal, /* netIsTaxInclusive */ false);
  return {
    method: 'on_total',
    label: config.label,
    rate: config.rate,
    inclusive: false,
    total: taxTotal,
    perLine,
  };
}

function perItemTax(
  ctx: TaxContext,
  config: Extract<TaxConfig, { method: 'per_item' }>,
): TaxBreakdown {
  const perLine: PerLineTax[] = [];
  let total = Money.zero(ctx.currency);

  for (let i = 0; i < ctx.lines.length; i++) {
    const line = ctx.lines[i]!;
    const input = ctx.inputs[i]!;
    const taxableForLine = perLineTaxable(line, ctx);

    if (Money.isZero(taxableForLine)) {
      perLine.push({ id: line.id, tax: Money.zero(ctx.currency), netAfterTax: line.net });
      continue;
    }

    const rate = validateRate(input.taxRate ?? 0, `LineItem ${line.id}`);
    if (rate === 0) {
      perLine.push({ id: line.id, tax: Money.zero(ctx.currency), netAfterTax: line.net });
      continue;
    }
    const rateStr = `${input.taxRate}`;

    if (config.inclusive) {
      const divisorPercent = `${100 + rate}`;
      const taxExcl = Money.div(
        Money.mul(taxableForLine, '100', ctx.mode),
        divisorPercent,
        ctx.mode,
      );
      const lineTax = Money.sub(taxableForLine, taxExcl);
      total = Money.add(total, lineTax);
      perLine.push({
        id: line.id,
        tax: lineTax,
        netAfterTax: Money.sub(line.net, lineTax),
      });
    } else {
      const lineTax = Money.div(
        Money.mul(taxableForLine, rateStr, ctx.mode),
        '100',
        ctx.mode,
      );
      total = Money.add(total, lineTax);
      perLine.push({ id: line.id, tax: lineTax, netAfterTax: line.net });
    }
  }

  return {
    method: 'per_item',
    label: config.label,
    inclusive: config.inclusive,
    total,
    perLine,
  };
}

function asDeductionTax(
  ctx: TaxContext,
  config: Extract<TaxConfig, { method: 'as_deduction' }>,
): TaxBreakdown {
  validateRate(config.rate, 'as_deduction');
  const rateStr = `${config.rate}`;
  const magnitude = Money.div(
    Money.mul(ctx.taxableBase, rateStr, ctx.mode),
    '100',
    ctx.mode,
  );
  const negated = Money.negate(magnitude);
  const perLine = allocateAcrossLines(ctx, negated, /* netIsTaxInclusive */ false);
  return {
    method: 'as_deduction',
    label: config.label,
    rate: config.rate,
    inclusive: false,
    total: negated,
    perLine,
  };
}

/**
 * Allocate `taxTotal` across taxable lines in proportion to each line's
 * post-invoice-discount taxable amount, carrying remainders
 * deterministically. Non-taxable lines get zero.
 */
function allocateAcrossLines(
  ctx: TaxContext,
  taxTotal: MoneyValue,
  netIsTaxInclusive: boolean,
): PerLineTax[] {
  const taxableLineAmounts = ctx.lines.map((l) => perLineTaxable(l, ctx));
  const positiveTotal = taxableLineAmounts.reduce(
    (acc, m) => acc + m.amount,
    0n,
  );
  if (positiveTotal === 0n || taxTotal.amount === 0n) {
    return ctx.lines.map((l) => ({
      id: l.id,
      tax: Money.zero(ctx.currency),
      netAfterTax: l.net,
    }));
  }

  // Use the absolute values for ratio math and re-apply sign to each
  // allocation bucket.
  const magnitude = taxTotal.amount < 0n ? -taxTotal.amount : taxTotal.amount;
  // Phase 10 (plan.md M1) — keep ratios as bigints. Going through
  // `Number(...)` collapses distinct amounts above 2^53 minor units
  // onto the same float, which silently rewrites the allocator
  // output as a 1:1 split for very large invoices. `Money.allocate`
  // accepts bigint ratios natively (see `money.ts`), so we just
  // pass them through.
  const ratios = taxableLineAmounts.map((m) => m.amount);
  // `positiveTotal === 0n` already guards the all-zero case above.
  const allocated = Money.allocate(Money.of(magnitude, ctx.currency), ratios);
  const signed =
    taxTotal.amount < 0n ? allocated.map((m) => Money.negate(m)) : allocated;

  return ctx.lines.map((l, i) => {
    const tax = signed[i] ?? Money.zero(ctx.currency);
    const net = netIsTaxInclusive ? Money.sub(l.net, tax) : l.net;
    return { id: l.id, tax, netAfterTax: net };
  });
}
